'use client';

import FaIcon, { faArrowUpFromBracket } from '@/components/icon/FaIcon/FaIcon';
import ShareActionButtons from '@/components/ui/ShareActionButtons/ShareActionButtons';
import { useIsSp } from '@/hooks/useMediaQuery';
import { swal } from '@/lib/sweetalert';
import { copyToClipboard, createDesignShareUrl } from '@/utils/share';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './DesignShareButton.module.scss';

function DesignShareButton({
  shareToken,
  disabled = false,
}: {
  shareToken: string;
  disabled?: boolean;
}) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isSp = useIsSp();
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const shareUrl = useMemo(() => createDesignShareUrl(shareToken), [shareToken]);

  const closePopover = useCallback(() => setIsPopoverOpen(false), []);

  const handleShareButtonClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!isSp) {
        setIsPopoverOpen((prev) => !prev);
        return;
      }

      if (!navigator.share) {
        await copyToClipboard(shareUrl);
        return;
      }

      try {
        await navigator.share({
          title: 'ジャンプLAB',
          text: '#ジャンプLAB',
          url: shareUrl,
        });
      } catch (error) {
        // ユーザーがシェアをキャンセルした場合や、ボタン連打で前のシェアが未完了の場合などに発生するエラーを無視する
        const ignoreErrors = ['AbortError', 'InvalidStateError', 'NotAllowedError'];
        if (error instanceof Error && ignoreErrors.includes(error.name)) {
          return;
        }
        await swal.alert({ text: 'シェアに失敗しました' });
      }
    },
    [isSp, shareUrl, copyToClipboard],
  );

  useEffect(() => {
    if (!isPopoverOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isOutside = !popoverRef.current?.contains(target) && !buttonRef.current?.contains(target);
      if (isOutside) closePopover();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPopoverOpen, closePopover]);

  return (
    <div className={styles.wrapper}>
      <button
        ref={buttonRef}
        type='button'
        className={styles.shareIconButton}
        onClick={handleShareButtonClick}
        aria-label='シェア'
        aria-expanded={!isSp ? isPopoverOpen : undefined}
        aria-haspopup={!isSp ? 'true' : undefined}
        disabled={disabled}
      >
        <FaIcon icon={faArrowUpFromBracket} />
      </button>
      {!isSp && isPopoverOpen && (
        <div ref={popoverRef} className={styles.popover}>
          <ShareActionButtons shareUrl={shareUrl} onShared={closePopover} direction='column' />
        </div>
      )}
    </div>
  );
}

export default DesignShareButton;
